import React, { useState, useRef, useEffect } from 'react';
import comentarioService from '../../services/comentario.service';

// ════════════════════════════════════════════════════════════════════════════
// ComentarioModal — r173
// Modal (abre por cima) para escrever um comentário ou uma resposta.
//   - Campo de texto (Ctrl+V cola imagem · Enter quebra linha)
//   - r180: Enter NAO envia — o envio é exclusivo do botão "Enviar", e o
//     fechamento é exclusivo do × (clicar fora não fecha).
//   - Anexos vinculados ao comentário (reusa o upload existente)
//   - Autocomplete de menção @: só solicitante e responsável da tarefa
//   - parentId != null → é uma resposta (fica aninhada abaixo do comentário pai)
// ════════════════════════════════════════════════════════════════════════════
const COR = {
  azul: '#003399', azulBg: '#eff6ff', borda: '#e8eaed', texto: '#1a1a2e',
  muted: '#888', branco: '#fff', cinzaBg: '#fafafa', vermelho: '#dc2626',
};

function ComentarioModal({ tarefaId, parentId, usuarioId, mencionaveis = [], onFechar, onSalvo }) {
  const [corpo, setCorpo] = useState('');
  const [anexosPendentes, setAnexosPendentes] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  // Menção (@)
  const [mencaoAberto, setMencaoAberto] = useState(false);
  const [mencaoQuery, setMencaoQuery] = useState('');

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { setTimeout(() => textareaRef.current?.focus(), 50); }, []);

  // ── Upload (botão 📎 ou paste) ────────────────────────────────────────────
  const fazerUpload = async (file, ehInline = false) => {
    try {
      setErro('');
      const meta = await comentarioService.upload(tarefaId, file);
      const novoAnexo = { ...meta, inline: ehInline };
      if (ehInline && meta.mime && meta.mime.startsWith('image/')) {
        const blobUrl = URL.createObjectURL(file);
        setCorpo(c => c + (c.endsWith('\n') || c.length === 0 ? '' : '\n') + `![${file.name}](${blobUrl})\n`);
        setAnexosPendentes(arr => [...arr, { ...novoAnexo, inline: true, blobUrl, file }]);
      } else {
        // r183: nao virou imagem inline no corpo (botao 📎, ou paste de algo
        // que nao e imagem) — tem que ir como anexo normal. Marcado como
        // inline sem placeholder no corpo, o arquivo sumia das duas pontas.
        setAnexosPendentes(arr => [...arr, { ...novoAnexo, inline: false }]);
      }
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Falha no upload: ' + err.message);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const ext = item.type.split('/')[1] || 'png';
          const renamed = new File([file], `colado-${Date.now()}.${ext}`, { type: item.type });
          fazerUpload(renamed, true);
        }
        return;
      }
    }
  };

  const handleSelecionarArquivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    fazerUpload(file, false);
    e.target.value = '';
  };

  const removerAnexoPendente = (idx) => setAnexosPendentes(arr => arr.filter((_, i) => i !== idx));

  // ── Menção @ ──────────────────────────────────────────────────────────────
  const onChangeCorpo = (e) => {
    const val = e.target.value;
    setCorpo(val);
    const pos = e.target.selectionStart;
    const m = val.slice(0, pos).match(/@(\S*)$/);
    if (m && mencionaveis.length > 0) {
      setMencaoQuery(m[1].toLowerCase());
      setMencaoAberto(true);
    } else {
      setMencaoAberto(false);
    }
  };

  const inserirMencao = (mc) => {
    const ta = textareaRef.current;
    const pos = ta ? ta.selectionStart : corpo.length;
    const antes = corpo.slice(0, pos).replace(/@(\S*)$/, `@${mc.email} `);
    const depois = corpo.slice(pos);
    setCorpo(antes + depois);
    setMencaoAberto(false);
    setTimeout(() => ta && ta.focus(), 0);
  };

  // r213: a lista de citáveis deixou de ser "solicitante + responsável" e passou
  //       a ser todos os usuários ativos, então o dropdown precisa de teto —
  //       sem isso um "@" sozinho despejaria centenas de linhas na tela.
  const MAX_SUGESTOES = 8;
  const mencaoTodas = mencionaveis.filter(mc =>
    !mencaoQuery
    || (mc.email || '').toLowerCase().includes(mencaoQuery)
    || (mc.nome || '').toLowerCase().includes(mencaoQuery)
  );
  const mencaoFiltrada = mencaoTodas.slice(0, MAX_SUGESTOES);

  // ── Enviar ────────────────────────────────────────────────────────────────
  const enviar = async () => {
    const c = corpo.trim();
    if (!c && anexosPendentes.length === 0) return;
    setEnviando(true);
    setErro('');
    try {
      let corpoFinal = c;
      const anexosInline = anexosPendentes.filter(a => a.inline);
      anexosInline.forEach((anx, i) => {
        if (anx.blobUrl) corpoFinal = corpoFinal.split(anx.blobUrl).join(`__INLINE_${i}__`);
      });
      const anexosParaEnviar = anexosPendentes.map(a => ({
        caminho: a.caminho, nome_original: a.nome_original,
        tamanho_bytes: a.tamanho_bytes, mime: a.mime, inline: a.inline ? 1 : 0,
      }));

      await comentarioService.criar(tarefaId, {
        corpo: corpoFinal,
        usuario_id: usuarioId,
        anexos: anexosParaEnviar,
        parent_id: parentId || null,
      });

      anexosPendentes.forEach(a => a.blobUrl && URL.revokeObjectURL(a.blobUrl));
      if (onSalvo) await onSalvo();
      onFechar();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao enviar.');
      setEnviando(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10050,
        backgroundColor: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Manrope', sans-serif",
      }}
    >
      <div style={{
        width: '640px', maxWidth: '94vw',
        backgroundColor: COR.branco, borderRadius: '8px', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh',
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: COR.azul, color: COR.branco,
          padding: '12px 18px', fontSize: '15px', fontWeight: '700',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{parentId ? '↳ Responder comentário' : 'Novo comentário'}</span>
          <button onClick={onFechar} title="Fechar" style={{
            background: 'transparent', border: 'none', color: COR.branco,
            fontSize: '22px', cursor: 'pointer', lineHeight: 1,
          }}>×</button>
        </div>

        {/* Corpo */}
        <div style={{ padding: '16px 18px', overflowY: 'auto' }}>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleSelecionarArquivo} />

          <div style={{ position: 'relative' }}>
            <textarea
              ref={textareaRef}
              value={corpo}
              onChange={onChangeCorpo}
              onPaste={handlePaste}
              placeholder="Escreva um comentário... (@ menciona · Ctrl+V cola imagens · Enter quebra linha)"
              disabled={enviando}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px',
                border: `1px solid ${COR.borda}`, borderRadius: '5px',
                fontSize: '13px', fontFamily: "'Manrope', sans-serif",
                resize: 'vertical', minHeight: '120px', maxHeight: '320px',
                lineHeight: '1.4',
              }}
            />
            {/* Dropdown de menção */}
            {mencaoAberto && mencaoFiltrada.length > 0 && (
              <div style={{
                position: 'absolute', left: '8px', bottom: '8px', zIndex: 5,
                background: COR.branco, border: `1px solid ${COR.borda}`,
                borderRadius: '6px', boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                minWidth: '240px', overflow: 'hidden',
              }}>
                <div style={{ padding: '4px 10px', fontSize: '10px', color: COR.muted, fontWeight: '700', backgroundColor: COR.cinzaBg }}>
                  CITAR COLABORADOR
                  {mencaoTodas.length > mencaoFiltrada.length && (
                    <span style={{ fontWeight: '600' }}>
                      {' '}— {mencaoTodas.length} encontrados, digite pra refinar
                    </span>
                  )}
                </div>
                {mencaoFiltrada.map(mc => (
                  <button key={mc.email} type="button" onClick={() => inserirMencao(mc)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '7px 10px', border: 'none', background: 'transparent',
                      cursor: 'pointer', fontSize: '12px', color: COR.texto,
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = COR.azulBg}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span style={{ fontWeight: '700' }}>{mc.nome || mc.email}</span>
                    <span style={{ color: COR.muted, marginLeft: '6px' }}>@{mc.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Anexos pendentes */}
          {anexosPendentes.length > 0 && (
            <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {anexosPendentes.map((a, i) => {
                const ehImg = a.mime && a.mime.startsWith('image/');
                return (
                  <div key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '4px 8px', backgroundColor: COR.cinzaBg,
                    border: `1px solid ${COR.borda}`, borderRadius: '4px', fontSize: '11px',
                  }}>
                    <span>{ehImg ? '🖼️' : '📎'}</span>
                    <span style={{ fontWeight: '600' }}>{a.nome_original}</span>
                    {a.inline && <span style={{ color: COR.azul, fontSize: '9px' }}>(inline)</span>}
                    <button onClick={() => removerAnexoPendente(i)} style={{
                      border: 'none', background: 'transparent', color: COR.muted,
                      cursor: 'pointer', fontSize: '13px', padding: '0 2px',
                    }}>×</button>
                  </div>
                );
              })}
            </div>
          )}

          {erro && (
            <div style={{
              padding: '6px 10px', marginTop: '8px',
              backgroundColor: '#fff0f0', border: `1px solid ${COR.vermelho}`,
              borderRadius: '4px', color: COR.vermelho, fontSize: '11px', fontWeight: '600',
            }}>{erro}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={enviando}
            style={{
              padding: '8px 14px', fontSize: '12px', fontWeight: '600',
              border: `1px solid ${COR.borda}`, borderRadius: '5px',
              backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
            }}>📎 Anexar</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button type="button" onClick={onFechar} disabled={enviando}
              style={{
                padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                border: `1px solid ${COR.borda}`, borderRadius: '5px',
                backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
              }}>Cancelar</button>
            <button type="button" onClick={enviar}
              disabled={enviando || (!corpo.trim() && anexosPendentes.length === 0)}
              style={{
                padding: '8px 20px', fontSize: '12px', fontWeight: '700',
                border: `1px solid ${COR.azul}`, borderRadius: '5px',
                backgroundColor: COR.azul, color: COR.branco,
                cursor: enviando ? 'wait' : 'pointer',
                opacity: (!corpo.trim() && anexosPendentes.length === 0) ? 0.5 : 1,
              }}>{enviando ? 'Enviando...' : (parentId ? 'Responder' : 'Comentar')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComentarioModal;
